import Image from "next/image";
import { AlertTriangle, Plug } from "lucide-react";
import { Badge } from "../../shadcn/badge";
import { cn } from "@/lib/utils";
import type { IntegrationInfo } from "./types";

export function IntegrationCard({
  integration,
  onClick,
  connecting,
  hideMcpBadge = false,
}: {
  integration: IntegrationInfo;
  onClick: (integration: IntegrationInfo) => void;
  connecting: string | null;
  hideMcpBadge?: boolean;
}) {
  const requiresAdmin = !integration.providers.some((p) => p.available);
  const showMcpBadge = integration.category === "MCP" && !hideMcpBadge;

  const handleClick = () => {
    if (requiresAdmin) return;
    onClick(integration);
  };

  const card = (
    <div
      className={cn(
        "flex items-center gap-3 p-3 pr-4 rounded-lg border border-border bg-card shadow-xs",
        requiresAdmin
          ? "opacity-60 cursor-not-allowed"
          : "cursor-pointer hover:border-ring"
      )}
      onClick={handleClick}
    >
      <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border/40 flex items-center justify-center p-1.5">
        <Image
          width={24}
          height={24}
          alt={integration.display_name}
          src={integration.image}
          style={{ objectFit: "contain", width: "100%", height: "100%" }}
        />
      </div>
      <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium text-sm text-foreground truncate">
            {integration.display_name}
          </span>
          {["netsuite", "xero", "sage", "microsoft-dynamics"].includes(
            integration.type
          ) && (
            <Badge
              variant="outline"
              className="text-[10px] px-1.5 py-0 h-5 font-semibold text-muted-foreground"
            >
              Pro
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showMcpBadge && (
            <Badge variant="outline">
              <Plug size={10} className="shrink-0" />
              MCP
            </Badge>
          )}
          {requiresAdmin && (
            <AlertTriangle size={14} className="text-warning-500" />
          )}
        </div>
      </div>
    </div>
  );

  return card;
}
